/// <reference path="../types.d.ts" />
import { syncActions, asyncActions, replaceOrganizationState } from '../actions/organization';
import { uiActions, commonEnums as enums } from '@codametrix/ui-common';
import InitialState from '../reducers/initial-state';
import { EMPTY_PAYLOAD } from '../actions/_action-utilities';
import { inProgress } from '../actions/common';
import { actions } from '../actions/users';
import { createReducer } from '@reduxjs/toolkit';
import { formErrors } from '../stubs/form-errors';
import { logout } from '../actions/ui';
import { jumpContext } from '../actions/contexts.action';
const { ORGS } = enums;
const childTypes = {};
childTypes[ORGS.BUSINESS] = new Set([
    ORGS.HEALTH_SYSTEM,
    ORGS.HOSPITAL
]);
childTypes[ORGS.HEALTH_SYSTEM] = new Set([
    ORGS.PHYSICIAN_ORGANIZATION,
    ORGS.HOSPITAL
]);
childTypes[ORGS.HOSPITAL] = new Set([
    ORGS.PHYSICIAN_ORGANIZATION
]);
childTypes[ORGS.PHYSICIAN_ORGANIZATION] = new Set();
function groupChildren(children, activeOrganization) {
    const childSets = {};
    const childOrgTypes = activeOrganization ? childTypes[activeOrganization.organizationType] : new Set(children.map((kid)=>kid.organizationType));
    [
        ...Array.from(childOrgTypes)
    ].forEach((orgType)=>childSets[orgType] = []);
    children.forEach((rawChild)=>{
        const child = rawChild.displayName ? rawChild : {
            ...rawChild,
            displayName: rawChild.organizationName
        };
        if (!childSets[child.organizationType]) {
            childSets[child.organizationType] = [];
        }
        childSets[child.organizationType].push(child);
    });
    [
        ...Array.from(childOrgTypes)
    ].forEach((orgType)=>childSets[orgType].sort((org1, org2)=>org1.displayName.localeCompare(org2.displayName)));
    return childSets;
}
function determineUserPath(state, organization) {
    const idx = state.path.findIndex((crumb)=>crumb.data.tenantId === organization.tenantId);
    let path;
    if (idx !== -1) {
        path = state.path.slice(0, idx + 1);
        path[path.length - 1] = {
            ...path[path.length - 1],
            display: false
        };
    } else {
        const current = {
            link: false,
            display: false,
            data: organization,
            displayName: `${organization.displayName}`
        };
        const allLinks = state.path.map((crumb)=>{
            return {
                ...crumb,
                link: true,
                display: true
            };
        });
        path = [
            ...allLinks,
            current
        ];
    }
    return path;
}
const handleChildren = (state, children)=>({
        ...state,
        organizations: groupChildren(children, state.activeOrganization)
    });
const handleOrganizationSelected = (state, organization)=>{
    const editable = state.activeOrganization.id === organization.parentId || state.activeOrganization.id === organization.id;
    const statePath = determineUserPath(state, organization);
    return {
        ...state,
        activeOrganization: organization,
        organizations: EMPTY_PAYLOAD,
        formErrors: formErrors.noErrors,
        editable,
        path: statePath,
        orgRelationships: [],
        associations: []
    };
};
export const reducer = createReducer(InitialState.organization, (builder)=>{
    builder.addCase(logout.type, ()=>({
            ...InitialState.organization
        }));
    builder.addCase(jumpContext.pending.type, ()=>({
            ...InitialState.organization
        }));
    builder.addCase(inProgress, (state)=>({
            ...state,
            formErrors: formErrors.noErrors
        }));
    builder.addCase(asyncActions.saveOrganization.rejected, (state, action)=>({
            ...state,
            formErrors: action.payload
        }));
    builder.addCase(asyncActions.doAssociation.rejected, (state, action)=>({
            ...state,
            formErrors: action.payload
        }));
    builder.addCase(syncActions.toggleEdit, (state, action)=>({
            ...state,
            edit: action.payload,
            addChild: false
        }));
    builder.addCase(syncActions.establishRelationship, (state, action)=>({
            ...state,
            relationship: action.payload
        }));
    builder.addCase(syncActions.orgRelationships, (state, action)=>{
        const now = Date.now();
        const relationships = action.payload.filter((relationship)=>{
            if (relationship.endDate === null) return true;
            return relationship.endDate > now;
        });
        return {
            ...state,
            orgRelationships: relationships
        };
    });
    builder.addCase(syncActions.serviceLinesAvailable, (state, action)=>({
            ...state,
            serviceLines: action.payload
        }));
    builder.addCase(syncActions.availableAssociations, (state, action)=>({
            ...state,
            associations: action.payload
        }));
    builder.addCase(syncActions.addChildOrgType, (state, action)=>({
            ...state,
            edit: false,
            addChild: action.payload,
            formErrors: formErrors.noErrors
        }));
    builder.addCase(syncActions.associationCreated, (state)=>({
            ...state,
            edit: false,
            addChild: false,
            formErrors: formErrors.noErrors
        }));
    builder.addCase(syncActions.organizationChildren, (state, action)=>handleChildren(state, action.payload));
    builder.addCase(syncActions.organizationSelected, (state, action)=>handleOrganizationSelected(state, action.payload));
    builder.addCase(uiActions.chooseContext.type, (state, action)=>{
        const context = action.payload;
        if (context.activeContext && state.path.length === 0) {
            return {
                ...state,
                activeOrganization: {
                    ...state.activeOrganization,
                    id: context.activeContext?.organizationId,
                    organizationName: context.activeContext?.displayName || state.activeOrganization.organizationName,
                    tenantId: context.activeContext?.tenantId || state.activeOrganization.tenantId
                }
            };
        }
        return {
            ...state
        };
    });
    builder.addCase(syncActions.timezonesAvailable, (state, action)=>({
            ...state,
            timezones: action.payload
        }));
    builder.addCase(syncActions.chooseDiagram, (state, action)=>({
            ...state,
            selectedDiagram: action.payload
        }));
    builder.addCase(asyncActions.getFeaturesBySection.fulfilled, (state, action)=>({
            ...state,
            features: action.payload ?? []
        }));
    builder.addCase(replaceOrganizationState, (state, action)=>({
            ...state,
            ...action.payload
        }));
    builder.addCase(actions.availableOrgs, (state, action)=>({
            ...state,
            activeOrganization: action.payload
        }));
});
export default reducer;
